import type { TutorBillingRecordChip } from './interfaces'

import { TutorBillingModal } from './enums'
import { useTutorBillingStore } from './store'
import { useTutorBillingBulkConfirm } from '../api/bulk-confirm.hook'
import { useTutorBillingListApi } from '../api/list.hook'
import { TUTOR_BILLING_BULK_LIMIT } from '../config/tutor-billing'
import { buildTutorBillingRecordChips } from '../lib/tutor-billing-fields'

interface ModalTutorBillingBulkConfirmReturn {
  isOpen: ComputedRef<boolean>
  isSaving: ComputedRef<boolean>
  chips: ComputedRef<TutorBillingRecordChip[]>
  isOverLimit: ComputedRef<boolean>
  isValid: ComputedRef<boolean>
  setClose: () => void
  setConfirm: () => Promise<void>
}

export const useModalTutorBillingBulkConfirm = (): ModalTutorBillingBulkConfirmReturn => {
  const store = useTutorBillingStore()

  const isOpen = computed<boolean>(() => store.isModal(TutorBillingModal.BulkConfirm))
  const isSaving = computed<boolean>(() => store.isSaving)

  const selectedIds = computed<number[]>(() => store.getSelectedIds)

  const chips = computed<TutorBillingRecordChip[]>(() => buildTutorBillingRecordChips(selectedIds.value, store.getRows))

  const isOverLimit = computed<boolean>(() => selectedIds.value.length > TUTOR_BILLING_BULK_LIMIT)

  const isValid = computed<boolean>(() => selectedIds.value.length > 0 && !isOverLimit.value)

  const setClose = (): void => {
    if (store.isSaving) return
    store.closeModal()
  }

  const setConfirm = async (): Promise<void> => {
    if (!isValid.value || store.isSaving) return

    const isConfirmed = await useTutorBillingBulkConfirm(selectedIds.value)

    if (!isConfirmed) return

    if (store.isModalFor(TutorBillingModal.BulkConfirm, null)) store.closeModal()
    store.clearSelection()
    void useTutorBillingListApi()
  }

  return {
    isOpen,
    isSaving,
    chips,
    isOverLimit,
    isValid,
    setClose,
    setConfirm,
  }
}
